import { setActiveSidebarTab } from 'client/actions/actionsManagement'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useWebsiteSettings } from 'client/store'
import { localeNames } from 'common/constants/localeTypes'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { TFunction } from 'i18next'
import React, { useState } from 'react'
import { BackButton } from 'src/editor/shared/components/back-button'
import { ChevronDownIcon } from 'src/editor/shared/icons/chevron-down-icon'
import { DotsVerticalIcon } from 'src/editor/shared/icons/dots-vertical-icon'
import { PlusCircleIcon } from 'src/editor/shared/icons/plus-circle-icon'
import { SearchInputIcon } from 'src/editor/shared/icons/search-input-icon'
import { ActionMenu } from 'src/editor/shared/ui-lib/action-menu'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import { Input } from 'src/editor/shared/ui-lib/input'
import { getLocalesOptions } from 'src/editor/shared/ui-lib/language-selector/utils/get-locales-options'
import { Switcher } from 'src/editor/shared/ui-lib/switcher'
import { setCurrentWebsiteLocale } from 'src/editor/store/website-settings/website-settings-actions'
import {
  getCurrentWebsiteLocale,
  getWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-selector'
import { WebsiteLanguageSwitcherIcon } from 'src/shared/entities/website-language-switcher/website-language-switcher-icon'
import { SidebarTabEnum } from 'src/shared/enums/website-enums'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { BlogCategoryMockItem } from 'src/shared/mocks/website-blog-categories.mock'
import { CategoriesListing } from './categories-listing'
import { BlogPostModal } from './components/blog-post-modal'
import { BlogPostModalFields } from './components/blog-post-modal.schema'
import { CategoryPostsListing } from './components/category-posts-listing'
import { CreateEditCategoryModal } from './components/create-edit-category-modal'
import { PostsListing } from './components/posts-listing'
import { SchedulePostModal } from './components/schedule-post-modal'
import {
  BlogActionItemEnum,
  BlogContentTabEnum,
  BlogPostFilterEnum,
} from './enums'
import { BlogCategoryFormModeEnum, BlogCategoryFormValues } from './types'

export const WebsiteBlogSettings = () => {
  const { t } = useTypedTranslation()
  const dispatch = useAppDispatch()
  const currentWebsiteLocale = useWebsiteSettings(
    getCurrentWebsiteLocale,
  ) as PageLocaleEnum
  const websiteLocales = useWebsiteSettings(getWebsiteLocales)

  const [activeTab, setActiveTab] = useState<BlogContentTabEnum>(
    BlogContentTabEnum.Posts,
  )
  const [activePostFilter] = useState<BlogPostFilterEnum>(
    BlogPostFilterEnum.AllPosts,
  )
  const [searchValue, setSearchValue] = useState('')
  const [selectedCategory, setSelectedCategory] =
    useState<BlogCategoryMockItem | null>(null)
  const [isBlogPostModalVisible, setBlogPostModalVisible] = useState(false)
  const [isEditingBlogPost, setIsEditingBlogPost] = useState(false)

  const [isCategoryModalOpened, setCategoryModalOpened] = useState(false)
  const [categoryFormMode, setCategoryFormMode] =
    useState<BlogCategoryFormModeEnum>(BlogCategoryFormModeEnum.Create)
  const [categoryInitialValues, setCategoryInitialValues] = useState<
    BlogCategoryFormValues | undefined
  >(undefined)

  const [isSchedulePostModalOpened, setSchedulePostModalOpened] =
    useState(false)
  const [schedulePostId, setSchedulePostId] = useState<string | null>(null)

  const closeCategoryModal = () => {
    setCategoryModalOpened(false)
  }

  const openCreateCategoryModal = () => {
    setCategoryFormMode(BlogCategoryFormModeEnum.Create)
    setCategoryInitialValues(undefined)
    setCategoryModalOpened(true)
  }

  const openEditCategoryModal = (values: BlogCategoryFormValues) => {
    setCategoryFormMode(BlogCategoryFormModeEnum.Edit)
    setCategoryInitialValues(values)
    setCategoryModalOpened(true)
  }

  const openSelectedCategoryEditModal = () => {
    if (!selectedCategory) {
      return
    }

    openEditCategoryModal({
      name: selectedCategory.name,
      path: selectedCategory.path,
      description: selectedCategory.description ?? '',
      seoTitle: selectedCategory.seoTitle ?? '',
      seoDescription: selectedCategory.seoDescription ?? '',
    })
  }

  const handleCreateBlogContent = () => {
    if (activeTab === BlogContentTabEnum.Categories) {
      openCreateCategoryModal()
    }
    if (activeTab !== BlogContentTabEnum.Posts) {
      return
    }
    setIsEditingBlogPost(false)
    setBlogPostModalVisible(true)
  }

  const handleBlogActionSelect = (action: BlogActionItemEnum) => {
    if (action === BlogActionItemEnum.Edit) {
      setIsEditingBlogPost(true)
      setBlogPostModalVisible(true)
    }
  }

  const handleSaveCategory = (values: BlogCategoryFormValues) => {
    // TODO: implement category saving API
  }
  // TODO: implement category saving API
  const closeBlogPostModal = () => {
    setBlogPostModalVisible(false)
  }

  const openPostSettings = (postId: string) => {
    setIsEditingBlogPost(true)
    setBlogPostModalVisible(true)
    // TODO: fetch post data by postId and populate initialValues
  }

  const openSchedulePostModal = (postId: string) => {
    setSchedulePostId(postId)
    setSchedulePostModalOpened(true)
  }

  const closeSchedulePostModal = () => {
    setSchedulePostModalOpened(false)
    setSchedulePostId(null)
  }

  const handleSchedulePostSubmit = (scheduledAt: Date) => {
    if (!schedulePostId) {
      closeSchedulePostModal()
      return
    }
    // TODO: implement schedule post API call for schedulePostId with scheduledAt
    void scheduledAt
    closeSchedulePostModal()
  }

  const handleBlogPostSubmit = (
    values: BlogPostModalFields,
    imageBlob?: Blob,
  ) => {
    // TODO: implement blog post save/update API
    closeBlogPostModal()
  }

  const localeOptions = getLocalesOptions({
    t: t as TFunction,
    localeList: websiteLocales,
  })

  const languageMenuItems = localeOptions.map(({ value, label }) => ({
    id: value,
    label,
    iconBefore: () => <WebsiteLanguageSwitcherIcon locale={value} />,
  }))

  return (
    <div className="flex h-full flex-col">
      <BackButton
        className="mb-4! shrink-0 self-start"
        onClick={() => {
          if (selectedCategory) {
            setSelectedCategory(null)
            return
          }
          dispatch(setActiveSidebarTab(SidebarTabEnum.PagesManagement))
        }}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {selectedCategory ? (
          <CategoryPostsListing
            category={selectedCategory}
            activePostFilter={activePostFilter}
            searchValue={searchValue}
            onOpenCategorySettings={openSelectedCategoryEditModal}
            onOpenPostSettings={openPostSettings}
            onSchedulePost={openSchedulePostModal}
          />
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0 flex-1 truncate text-heading-4">
                {t('entity_settings.blog.title')}{' '}
                {/* TODO: set blog name if needed after discussion with PO */}
              </span>
              <div className="flex shrink-0 items-center gap-1">
                <ActionMenu
                  items={languageMenuItems}
                  onSelect={locale =>
                    dispatch(setCurrentWebsiteLocale(locale as PageLocaleEnum))
                  }
                  triggerElement={setIsOpened => (
                    <button
                      type="button"
                      onClick={() => setIsOpened(true)}
                      className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-M text-textPrimary transition-all duration-150 hover:bg-bgSecondary"
                      title={t(localeNames[currentWebsiteLocale])}
                    >
                      <WebsiteLanguageSwitcherIcon
                        locale={currentWebsiteLocale}
                      />
                      <ChevronDownIcon className="h-4 w-4" />
                    </button>
                  )}
                />
                <ActionMenu
                  items={[
                    {
                      id: BlogActionItemEnum.Edit,
                      label: t('website_settings.sidebar.blog.actions.edit'),
                    },
                  ]}
                  onSelect={action =>
                    handleBlogActionSelect(action as BlogActionItemEnum)
                  }
                  triggerElement={setIsOpened => (
                    <GhostIconButton
                      onClick={() => setIsOpened(true)}
                      icon={<DotsVerticalIcon className="fill-iconSecondary" />}
                      title={t(
                        'website_settings.sidebar.pages_management.settings',
                      )}
                    />
                  )}
                />
              </div>
            </div>
            <hr className="border-border200" />
            <div className="flex items-center justify-between">
              <span className="text-heading-4">
                {t('website_settings.sidebar.blog.content.title')}
              </span>
              <GhostIconButton
                onClick={handleCreateBlogContent}
                icon={<PlusCircleIcon />}
              />
            </div>
            <Switcher
              btnList={[
                {
                  value: BlogContentTabEnum.Posts,
                  content: t('website_settings.sidebar.blog.content.posts'),
                },
                {
                  value: BlogContentTabEnum.Categories,
                  content: t(
                    'website_settings.sidebar.blog.content.categories',
                  ),
                },
              ]}
              value={activeTab}
              update={setActiveTab}
            />
            {activeTab === BlogContentTabEnum.Posts && (
              <PostsListing
                activePostFilter={activePostFilter}
                searchValue={searchValue}
                onOpenPostSettings={openPostSettings}
                onSchedulePost={openSchedulePostModal}
              />
            )}
            {activeTab === BlogContentTabEnum.Categories && (
              <Input
                type="search"
                value={searchValue}
                onChange={setSearchValue}
                placeholder={t('global.search')}
                endElement={<SearchInputIcon />}
              />
            )}
            {activeTab === BlogContentTabEnum.Categories && (
              <CategoriesListing
                onEditCategory={openEditCategoryModal}
                onOpenCategory={setSelectedCategory}
              />
            )}
          </>
        )}
      </div>
      <CreateEditCategoryModal
        isOpened={isCategoryModalOpened}
        mode={categoryFormMode}
        initialValues={categoryInitialValues}
        onClose={closeCategoryModal}
        onSubmit={handleSaveCategory}
      />
      <BlogPostModal
        isVisible={isBlogPostModalVisible}
        isEditing={isEditingBlogPost}
        categories={[]}
        onClose={closeBlogPostModal}
        onSubmit={handleBlogPostSubmit}
      />
      <SchedulePostModal
        isOpened={isSchedulePostModalOpened}
        onClose={closeSchedulePostModal}
        onSubmit={handleSchedulePostSubmit}
      />
    </div>
  )
}
